import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  ListSubheader,
  Checkbox,
  ListItemText,
} from "@mui/material";

import ShuffleIcon from "@mui/icons-material/Shuffle";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CelebrationIcon from "@mui/icons-material/Celebration";

import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";

import { useAuth } from "../../../AuthProvider";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

export default function FinalSelectedQuestions() {
  const navigate = useNavigate();
  const { TestType } = useParams();
  const { oId, pId } = useAuth();

  const [questionsList, setQuestionsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("shuffle");
  const [submitting, setSubmitting] = useState(false);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState("");
  const [customDuration, setCustomDuration] = useState("");

  const [durationOptions, setDurationOptions] = useState([
    "30 minutes",
    "1 hour",
    "1.5 hours",
    "2 hours",
  ]);

  /* ================= CLASS STATE ================= */
  const [classData, setClassData] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  

  /* ================= FETCH QUESTIONS ================= */
  const fetchQuestions = useCallback(async () => {
    if (!TestType) return;

    try {
      setLoading(true);

      const res = await fetch(
        `${baseUrl}/api/online/exam/all/${oId}/${pId}?type=${TestType}`,
      );

      if (!res.ok) throw new Error("Failed");

      const data = await res.json();

      const formatted = data.map((q, index) => ({
        onlineId: q.onlineId || index + 1,
        question: q.question,
        options: [q.option1, q.option2, q.option3, q.option4],
        answerKey: q.answerKey || "",
        type: q.type,

        // ✅ ADD THESE
        date: q.date,
        time: q.time,
        duration: q.duration,
        romanNumberClass: q.romanNumberClass,
      }));

      setQuestionsList(formatted);
    } catch (err) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, [TestType, oId, pId]);

  /* ================= FETCH CLASSES ================= */
  const fetchClasses = useCallback(async () => {
    try {
      const res = await fetch(
        `${baseUrl}/api/dynamic-classReg/class-registrations/${oId}/${pId}`,
      );

      if (!res.ok) throw new Error();

      const data = await res.json();

      // Convert sections string → array
      const formatted = data.map((item) => ({
        class: item.classRegistration,
        sections: item.sections.split(","),
      }));

      setClassData(formatted);
    } catch {
      toast.error("Failed to load classes");
    }
  }, [oId, pId]);

  useEffect(() => {
    fetchQuestions();
    fetchClasses();
  }, [fetchQuestions, fetchClasses]);

  /* ================= RELEASE ================= */
  const handleReleaseQuestions = async () => {
    if (!date || !time || !duration || selectedClasses.length === 0) {
      toast.warn("Fill all fields");
      return;
    }

    try {
      setSubmitting(true);

      const res = await fetch(
        `${baseUrl}/api/online/exam/release/${pId}/${TestType}/${mode}?date=${date}&time=${time}&duration=${duration}&classes=${selectedClasses}`,
        { method: "PUT" },
      );

      if (!res.ok) throw new Error();

      toast.success("Released Successfully");
      fetchQuestions()
      setOpen(false);
    } catch {
      toast.error("Release failed");
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= DURATION ================= */
  const handleAddDuration = () => {
    const val = customDuration.trim();
    if (!val || durationOptions.includes(val)) return;

    setDurationOptions((prev) => [...prev, val]);
    setDuration(val);
    setCustomDuration("");
  };

  /* ================= UI ================= */
  return (
    <Box sx={{ m: "5% 1%" }}>
      <ToastContainer />

      <Card>
        <CardContent>
          {/* HEADER */}
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate(-1)}>
                <KeyboardBackspaceIcon />
              </IconButton>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800, // 🔥 extra bold
                  letterSpacing: "0.5px", // optional for thickness feel
                  color: "#000000", // optional nice dark color
                }}
              >
                Final {TestType} Questions
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              startIcon={<ShuffleIcon />}
              sx={{ backgroundColor: "#3C3972" }}
            >
              Shuffle
            </Button>
          </Box>

          {/* 🔥 EXAM DETAILS CARD */}
          {questionsList.length > 0 && (
            <Box
              sx={{
                background: "linear-gradient(135deg, #3C3972, #5A54A4)",
                borderRadius: "16px",
                padding: "7px",
                color: "#fff",
                marginBottom: "16px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                📘 Exam Details
              </Typography>

              <Box
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                {/* Class */}
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Class
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {questionsList[0].romanNumberClass}
                  </Typography>
                </Box>

                {/* Date */}
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Date
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {questionsList[0].date}
                  </Typography>
                </Box>

                {/* Time */}
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Time
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {questionsList[0].time}
                  </Typography>
                </Box>

                {/* Duration */}
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Duration
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {questionsList[0].duration}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* QUESTIONS */}
          {loading ? (
            <CircularProgress />
          ) : (
            questionsList.map((q, i) => (
              <motion.div key={q.onlineId}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {i + 1}. {q.question}
                  </AccordionSummary>

                  <AccordionDetails>
                    {q.options.map(
                      (opt, idx) =>
                        opt && (
                          <Typography key={idx}>
                            {String.fromCharCode(97 + idx)}. {opt}
                          </Typography>
                        ),
                    )}
                    <Typography color="green">Answer: {q.answerKey}</Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))
          )}
        </CardContent>
      </Card>

      {/* DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>
          Release Questions
          <IconButton onClick={() => setOpen(false)} sx={{ float: "right" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* MODE */}
          <Box display="flex" gap={2} mb={2}>
            <Button
              variant={mode === "straight" ? "contained" : "outlined"}
              onClick={() => setMode("straight")}
              startIcon={<FormatListNumberedIcon />}
              sx={{
                flex: 1,
                fontWeight: "bold",
                backgroundColor: mode === "straight" ? "#3C3972" : "",
                color: mode === "straight" ? "#fff" : "#2e2a54",
                borderColor: "#2e2a54",
                "&:hover": {
                  backgroundColor: mode === "straight" ? "#1f1b3a" : "#f5f5f5",
                },
              }}
            >
              Straight
            </Button>

            <Button
              variant={mode === "shuffle" ? "contained" : "outlined"}
              onClick={() => setMode("shuffle")}
              startIcon={<ShuffleIcon />}
              sx={{
                flex: 1,
                fontWeight: "bold",
                backgroundColor: mode === "shuffle" ? "#3C3972" : "",
                color: mode === "shuffle" ? "#fff" : "#2e2a54",
                borderColor: "#2e2a54",
                "&:hover": {
                  backgroundColor: mode === "shuffle" ? "#1f1b3a" : "#f5f5f5",
                },
              }}
            >
              Shuffle
            </Button>
          </Box>
          {/* DATE */}
          <TextField
            type="date"
            fullWidth
            value={date}
            onChange={(e) => setDate(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* TIME + CLASS (SIDE BY SIDE) */}
          <Box display="flex" gap={2} mb={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Time"
                ampm
                value={time ? dayjs(time, "hh:mm A") : null}
                onChange={(v) => setTime(v ? v.format("hh:mm A") : "")}
                sx={{ flex: 1 }}
              />
            </LocalizationProvider>

            {/* CLASS MULTI SELECT */}
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Classes</InputLabel>
              <Select
                multiple
                value={selectedClasses}
                onChange={(e) => setSelectedClasses(e.target.value)}
                input={<OutlinedInput label="Classes" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {classData.map((cls, i) => (
                  <MenuItem key={i} value={cls.class}>
                    <Checkbox checked={selectedClasses.includes(cls.class)} />
                    <ListItemText primary={cls.class} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* DURATION */}
          <FormControl fullWidth>
            <InputLabel>Duration</InputLabel>
            <Select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              input={<OutlinedInput label="Duration" />}
            >
              <ListSubheader>
                <Box display="flex">
                  <TextField
                    size="small"
                    value={customDuration}
                    onChange={(e) => setCustomDuration(e.target.value)}
                  />
                  <IconButton onClick={handleAddDuration}>
                    <AddIcon />
                  </IconButton>
                </Box>
              </ListSubheader>

              {durationOptions.map((opt, i) => (
                <MenuItem key={i} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* SUBMIT */}
          <Button
            fullWidth
            sx={{ mt: 3, backgroundColor: "#3C3972" }}
            variant="contained"
            onClick={handleReleaseQuestions}
            disabled={submitting}
            endIcon={<CelebrationIcon />}
          >
            {submitting ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
